// Unit and status helpers for the all-parameters history page (feedback
// #60). Storage is canonical (ppt, °F); every number on that page is in
// the keeper's own unit: their salinity scale on this device, °C on a
// Celsius tank. Pure, so the conversions are tested, not re-derived.
import { pptToSg, salinityUnitLabel } from './salinity.js';
import { fToC } from './units.js';

// `sgDecimals` lets a page that prints an S.G. band at four places print
// its values at four too (salinity.js sgDecimals).
export function toDisplayValue(
  key,
  value,
  { salinityUnit = 'ppt', tempUnit = 'F', sgDecimals = 3 } = {}
) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (key === 'salinity') {
    return salinityUnit === 'sg' ? pptToSg(n, sgDecimals) : Math.round(n * 10) / 10;
  }
  if (key === 'temp' && tempUnit === 'C') return Math.round(fToC(n) * 10) / 10;
  return Math.round(n * 1000) / 1000;
}

export function displayUnit(key, storedUnit, { salinityUnit = 'ppt', tempUnit = 'F' } = {}) {
  if (key === 'salinity') return salinityUnitLabel(salinityUnit);
  if (key === 'temp') return tempUnit === 'C' ? '°C' : '°F';
  return storedUnit || '';
}

// The all-history page's range buttons (AllHistoryView RANGES), and the
// shortest one reaching back to `firstTs`, so a page opened straight after
// an import shows what was imported (0 = all).
export const HISTORY_RANGES = [7, 30, 90, 365, 0];
export function rangeCovering(firstTs, now = Date.now()) {
  const t = Date.parse(firstTs);
  if (!Number.isFinite(t)) return 30;
  const days = (now - t) / 86400000;
  return HISTORY_RANGES.find((k) => k > 0 && days <= k) ?? 0;
}

// A reading, or a bucketed median with its min and max, in display units.
export function pointToDisplay(key, point, opts) {
  return {
    ...point,
    // The row's own stored unit (ppt, °F) would label a converted value.
    unit: displayUnit(key, point.unit, opts),
    value: toDisplayValue(key, point.value, opts),
    ...(point.min != null ? { min: toDisplayValue(key, point.min, opts) } : {}),
    ...(point.max != null ? { max: toDisplayValue(key, point.max, opts) } : {}),
  };
}

// The card-side view of a param (target and band) in display units, so the
// shaded band sits where the converted line is.
export function paramToDisplay(key, p, opts) {
  if (!p) return p;
  const band =
    Array.isArray(p.acceptable) && p.acceptable.length === 2
      ? p.acceptable.map((v) => toDisplayValue(key, v, opts))
      : p.acceptable;
  return {
    ...p,
    unit: displayUnit(key, p.unit, opts),
    target: p.target != null ? toDisplayValue(key, p.target, opts) : p.target,
    acceptable: band,
  };
}

// The pill, in the words Home uses. `st` is paramStatusFor's answer.
export function historyPill({ hasReading, stale, staleReason = null, st, cycling, value, band }) {
  if (!hasReading) return 'TBD';
  if (stale) return staleReason === 'test_time_unknown' ? 'TEST TIME?' : 'STALE';
  if (st === 'off' && Array.isArray(band)) return Number(value) > Number(band[1]) ? 'HIGH' : 'LOW';
  if (st === 'ok') return 'OK';
  if (st === 'tracked') return cycling ? 'CYCLING' : 'LOGGED';
  if (st === 'implausible') return 'CHECK';
  return 'TBD';
}
